import { PlayerPosition } from 'modules/teams/domain/enums/player-position';
import { TournamentUser } from 'modules/teams/domain/interfaces/tournament-user';
import { changePlayerRole, deleteTournamentPlayerApplication } from 'modules/teams/store/players/actions';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { Role } from 'shared/types/role';
import { getFullName } from 'shared/utils';
import { AsyncState } from 'shared/utils/async-state';

import { AppDispatch } from 'store/store';
import { UDEntityList } from 'ud-ui/components';
import ParticipantRow from './participant-row';

interface Props {
  teamId: number;
  participants?: TournamentUser[];
}

const TeamsComponentsFormParticipantsListPart: React.FC<Props> = ({ teamId, participants = [] }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const [filterValue, setFilterValue] = useState('');
  const [loadingParticipantId, setLoadingParticipantId] = useState<number | null>(null);

  const changeRole = useCallback(
    (participant: TournamentUser, role: string) => {
      try {
        const number = participant.number ?? 0;
        const position = participant.position ?? PlayerPosition.MIDFIELDER;

        setLoadingParticipantId(participant.id);

        dispatch(
          changePlayerRole({
            teamId,
            userId: participant.id,
            role: role as Role,
            number,
            position: position?.toLowerCase() as PlayerPosition,
          }),
        )
          .unwrap()
          .then(() => {
            toast.success(
              t('participant.changeRole', {
                participant: getFullName(participant),
                role: t(`player.role.${role}`),
              }),
            );
          })
          .catch(() => {
            toast.error(
              t('participant.changeRoleError', {
                participant: getFullName(participant),
              }),
            );
          })
          .finally(() => {
            setLoadingParticipantId(null);
          });
      } catch (error) {
        setLoadingParticipantId(null);
        toast.error(
          t('participant.changeRoleError', {
            participant: getFullName(participant),
          }),
        );
      }
    },
    [dispatch, t, teamId],
  );

  const kickFromTeam = useCallback(
    (participant: TournamentUser) => {
      try {
        setLoadingParticipantId(participant.id);

        dispatch(deleteTournamentPlayerApplication({ teamId, userId: participant.id }))
          .unwrap()
          .then(() => {
            toast.success(
              t('participant.kickFromTeam', {
                participant: getFullName(participant),
              }),
            );
          })
          .catch(() => {
            toast.error(
              t('participant.kickFromTeamError', {
                participant: getFullName(participant),
              }),
            );
          })
          .finally(() => {
            setLoadingParticipantId(null);
          });
      } catch (error) {
        setLoadingParticipantId(null);
        toast.error(
          t('participant.kickFromTeamError', {
            participant: getFullName(participant),
          }),
        );
      }
    },
    [dispatch, t, teamId],
  );

  const participantsByFilter = useMemo(() => {
    return participants.filter((participant) => {
      return getFullName(participant).toLowerCase().includes(filterValue.toLowerCase());
    });
  }, [participants, filterValue]);

  // Вкладка получает готовый массив и сети не видит: ветки загрузки и ошибки рисует
  // карточка команды выше по дереву (`team-edit-page.tsx`). Считать надо по
  // отфильтрованному списку, иначе ветка «не найдено» никогда не покажется.
  const state: AsyncState = useMemo(
    () => ({ status: participantsByFilter.length > 0 ? 'ready' : 'empty', isRefreshing: false }),
    [participantsByFilter.length],
  );

  const renderParticipant = useCallback(
    (participant: TournamentUser, index: number) => (
      <ParticipantRow
        participant={participant}
        index={index + 1}
        isLoading={loadingParticipantId === participant.id}
        onChangeRole={changeRole}
        onKick={kickFromTeam}
      />
    ),
    [changeRole, kickFromTeam, loadingParticipantId],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col pb-6">
      <UDEntityList<TournamentUser>
        items={participantsByFilter}
        renderItem={renderParticipant}
        getItemKey={(participant) => participant.id}
        state={state}
        search={filterValue}
        onSearchChange={setFilterValue}
        searchPlaceholder={t('participant.filter.inputPlaceholder')}
        emptyText={t('participant.empty')}
        notFoundText={t('participant.notFound')}
      />
    </div>
  );
};

export default TeamsComponentsFormParticipantsListPart;
